import moment from "moment-timezone";
import { TimeSchedule } from "@api/graphql/generated/generated-types";

const ITERATION_CAP = 400;

const ISO_WEEKDAY_TO_DAY_NAME: Record<number, string> = {
  1: "MON",
  2: "TUE",
  3: "WED",
  4: "THU",
  5: "FRI",
  6: "SAT",
  7: "SUN",
};

type Window = { openSec: number; closeSec: number };

function indexSchedule(schedule: TimeSchedule): Map<string, Window[]> {
  const map = new Map<string, Window[]>();
  for (const entry of schedule.weekDailyList ?? []) {
    if (!entry) continue;
    const window: Window = {
      openSec: entry.startTimeSecondsSinceMidnight,
      closeSec: entry.endTimeSecondsSinceMidnight,
    };
    for (const day of entry.days ?? []) {
      const key = String(day);
      const list = map.get(key) ?? [];
      list.push(window);
      map.set(key, list);
    }
  }
  map.forEach((list) => {
    list.sort((a, b) => a.openSec - b.openSec);
  });
  return map;
}

/**
 * Adds `durationMinutes` to `start`, counting only time inside the schedule's
 * open windows. If `start` is outside any open window, the clock begins at
 * the next open instant. Uses moment's default timezone.
 */
export function plusBusinessMinutes(
  start: moment.Moment,
  durationMinutes: number,
  schedule: TimeSchedule
): moment.Moment {
  if (durationMinutes <= 0) return start.clone();

  const windowsByDay = indexSchedule(schedule);
  let remaining = durationMinutes * 60;
  let cursor = start.clone();

  for (let i = 0; i < ITERATION_CAP; i++) {
    const { windowOpen, windowClose } = findNextOpenWindow(
      cursor,
      windowsByDay
    );
    if (cursor.isBefore(windowOpen)) {
      cursor = windowOpen.clone();
    }
    const secondsUntilClose = windowClose.diff(cursor, "seconds");
    if (remaining <= secondsUntilClose) {
      return cursor.clone().add(remaining, "seconds");
    }
    remaining -= secondsUntilClose;
    cursor = windowClose.clone();
  }

  throw new Error(
    `plusBusinessMinutes exceeded iteration cap (${ITERATION_CAP}). ` +
      `durationMinutes=${durationMinutes}, start=${start.toISOString()}`
  );
}

function findNextOpenWindow(
  cursor: moment.Moment,
  windowsByDay: Map<string, Window[]>
): { windowOpen: moment.Moment; windowClose: moment.Moment } {
  for (let dayOffset = 0; dayOffset < 14; dayOffset++) {
    const candidate = cursor.clone().add(dayOffset, "days");
    const dayName = ISO_WEEKDAY_TO_DAY_NAME[candidate.isoWeekday()];
    const windows = windowsByDay.get(dayName) ?? [];
    const baseOfDay = candidate.clone().startOf("day");
    for (const w of windows) {
      const openAt = baseOfDay.clone().add(w.openSec, "seconds");
      const closeAt = baseOfDay.clone().add(w.closeSec, "seconds");
      if (cursor.isBefore(closeAt)) {
        return { windowOpen: openAt, windowClose: closeAt };
      }
    }
  }
  throw new Error(
    "plusBusinessMinutes: no open window found within 14 days of cursor"
  );
}
